import { create } from 'zustand';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import { useInterfaceStore } from '../interface/interfaceStore';

const GUEST_MODE_KEY = 'tdee-guest-mode';
const LOGIN_TIMESTAMP_KEY = 'tdee-login-timestamp';
const SESSION_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  authReady: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  continueAsGuest: () => void;
}

const wasGuest = localStorage.getItem(GUEST_MODE_KEY) === 'true';

const setLoginTimestamp = () => {
  localStorage.setItem(LOGIN_TIMESTAMP_KEY, String(Date.now()));
};

const clearLoginTimestamp = () => {
  localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
};

const isSessionExpired = (): boolean => {
  const timestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
  if (!timestamp) return false;
  return Date.now() - Number(timestamp) > SESSION_DURATION_MS;
};

export const initAuthListener = (): (() => void) =>
  onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      if (isSessionExpired()) {
        clearLoginTimestamp();
        localStorage.removeItem(GUEST_MODE_KEY);
        await auth.signOut();
        useAuthStore.setState({
          user: null,
          isAuthenticated: false,
          isGuest: false,
          authReady: true,
        });
        return;
      }

      if (!localStorage.getItem(LOGIN_TIMESTAMP_KEY)) {
        setLoginTimestamp();
      }

      localStorage.removeItem(GUEST_MODE_KEY);
      useAuthStore.setState({
        user: firebaseUser,
        isAuthenticated: true,
        isGuest: false,
        authReady: true,
      });
      return;
    }

    const guest = localStorage.getItem(GUEST_MODE_KEY) === 'true';
    useAuthStore.setState({
      user: null,
      isAuthenticated: guest,
      isGuest: guest,
      authReady: true,
    });
  });

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: wasGuest,
  isGuest: wasGuest,
  authReady: false,

  signIn: async (email: string, password: string) => {
    const { fetchDataStart, fetchDataSuccess, fetchDataFailure } = useInterfaceStore.getState();
    fetchDataStart();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      localStorage.removeItem(GUEST_MODE_KEY);
      setLoginTimestamp();
      set({ user: userCredential.user, isAuthenticated: true, isGuest: false });
      fetchDataSuccess();
    } catch (error) {
      fetchDataFailure('Invalid email or password');
      throw error;
    }
  },

  signOut: async () => {
    await auth.signOut();
    localStorage.removeItem(GUEST_MODE_KEY);
    clearLoginTimestamp();
    set({ user: null, isAuthenticated: false, isGuest: false });
  },

  register: async (email: string, password: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    localStorage.removeItem(GUEST_MODE_KEY);
    setLoginTimestamp();
    set({ user: userCredential.user, isAuthenticated: true, isGuest: false });
  },

  sendPasswordReset: async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed) {
      throw new Error('Enter your email address');
    }
    await sendPasswordResetEmail(auth, trimmed);
  },

  continueAsGuest: () => {
    localStorage.setItem(GUEST_MODE_KEY, 'true');
    set({ user: null, isAuthenticated: true, isGuest: true });
  },
}));

export const useAuth = () => ({
  user: useAuthStore(state => state.user),
  isAuthenticated: useAuthStore(state => state.isAuthenticated),
  isGuest: useAuthStore(state => state.isGuest),
  authReady: useAuthStore(state => state.authReady),
  signIn: useAuthStore(state => state.signIn),
  signOut: useAuthStore(state => state.signOut),
  register: useAuthStore(state => state.register),
  sendPasswordReset: useAuthStore(state => state.sendPasswordReset),
  continueAsGuest: useAuthStore(state => state.continueAsGuest),
})